import Icon, { ArrowRightOutlined } from '@ant-design/icons';
import { WorkspaceFramework } from '../../../../types/Workspace';
import { ItemType } from 'antd/lib/menu/interface';
import { ExpandDown } from '../../../../assets/CustomIcons/ExpandDown';
import {
  MigrationStatusColorMap,
  MigrationStatusLabel,
  MigrationStatusTextMap,
  MigrationStatusType,
} from '../components/MigrationStatusLabel';
import { useState } from 'react';
import BaseDropdown from '../../../../components/UI/Dropdown/BaseDropdown';

export type MigrationStatusMapping = Partial<Record<MigrationStatusType, MigrationStatusType>>;
export type TaskStatusCount = Partial<Record<MigrationStatusType, number>>;

export type SpaceTasksMigrationProps = {
  selectedFramework: WorkspaceFramework;
  fromFramework: WorkspaceFramework;
  statusesMapping: MigrationStatusMapping;
  taskStatusCount: TaskStatusCount;
  setStatusesMapping: (mapping: MigrationStatusMapping) => void;
};

export const SpaceTasksMigration = ({
  selectedFramework,
  fromFramework,
  statusesMapping,
  taskStatusCount,
  setStatusesMapping,
}: SpaceTasksMigrationProps) => {
  let description = '';
  switch (selectedFramework) {
    case WorkspaceFramework.scrum:
      description =
        'Sprint framework fundamentally differs to Kanban by having Planning and Sprint phases within it’s workflow.  It is most used by teams who plan their work in advance.';
      break;
    case WorkspaceFramework.kanban:
      description =
        'Kanban framework fundamentally differs to Sprint by not having Planning and Sprint phases within it’s workflow. It is most used by teams who need to track continuous task delivery.';
      break;
    case WorkspaceFramework.none:
      description =
        fromFramework === WorkspaceFramework.scrum
          ? 'Deciding to go without a framework will remove your ability to track tasks within Planning and Sprint phases. If you proceed, all your epics and tasks will be tracked within a single Backlog tab.'
          : 'Deciding to  go without a framework will remove your ability to track tasks within Kanban phase. If you proceed, all your epics and tasks will be tracked within a single Backlog tab.';
      break;
  }

  const setStatus = (from: MigrationStatusType, to: MigrationStatusType) => {
    setStatusesMapping({
      ...statusesMapping,
      [from]: to,
    });
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="text-neutral-900 text-sm font-normal leading-tight">{description}</div>

        <div className="flex flex-col gap-2 mt-4">
          {Object.entries(statusesMapping).map(([from, to]) => (
            <StatusMigrationComponent
              selectedFramework={selectedFramework}
              statusFrom={from as MigrationStatusType}
              statusTo={to}
              setStatus={setStatus}
              numOfTasks={taskStatusCount[from as MigrationStatusType] || 0}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export type StatusMigrationProps = {
  selectedFramework: WorkspaceFramework;
  statusFrom: MigrationStatusType;
  statusTo: MigrationStatusType;
  setStatus: (from: MigrationStatusType, to: MigrationStatusType) => void;
  numOfTasks: number;
};

const StatusMigrationComponent = ({
  selectedFramework,
  statusFrom,
  statusTo,
  setStatus,
  numOfTasks,
}: StatusMigrationProps) => {
  return (
    <div className="flex flex-col items-start mt-2 gap-1">
      <div className="text-neutral-900 text-xs font-normal leading-none">{`${numOfTasks} ${numOfTasks > 1 ? 'tasks' : 'task'}`}</div>
      <div className="flex gap-2">
        <MigrationStatusLabel migrationStatus={statusFrom} component={'table'}></MigrationStatusLabel>
        <ArrowRightOutlined />
        {selectedFramework === WorkspaceFramework.none && (
          <MigrationStatusLabel migrationStatus={statusTo} component={'table'}></MigrationStatusLabel>
        )}
        {selectedFramework !== WorkspaceFramework.none && (
          <FrameworkStatusLabelDropdown
            selectedFramework={selectedFramework}
            status={statusTo}
            setStatusTo={(to) => setStatus(statusFrom, to)}
          ></FrameworkStatusLabelDropdown>
        )}
      </div>
    </div>
  );
};

export type FrameworkStatusLabelProps = {
  selectedFramework: WorkspaceFramework;
  status: MigrationStatusType;
  setStatusTo: (status: MigrationStatusType) => void;
};

const FrameworkStatusLabelDropdown = ({ selectedFramework, status, setStatusTo }: FrameworkStatusLabelProps) => {
  const [selectedStatus, setSelectedStatus] = useState<MigrationStatusType>(status);
  const possibleStatuses: MigrationStatusType[] =
    selectedFramework === WorkspaceFramework.scrum ? ['backlog', 'planning', 'sprint'] : ['backlog', 'kanban'];
  const menu: ItemType[] = possibleStatuses.map((status) => {
    return {
      key: status,
      name: MigrationStatusTextMap[status],
      label: <MigrationStatusLabel migrationStatus={status} component="dropdown" />,
    };
  });
  const bgColor = MigrationStatusColorMap[selectedStatus].background;
  return (
    <BaseDropdown
      menu={{
        items: menu,
        onClick: (e) => {
          setSelectedStatus(e.key as MigrationStatusType);
          setStatusTo(e.key as MigrationStatusType);
        },
      }}
      trigger={['click']}
    >
      <div className={`flex rounded cursor-pointer ${bgColor}`}>
        <MigrationStatusLabel migrationStatus={selectedStatus} component={'table'} />
        <Icon component={ExpandDown} className="mr-0.5" />
      </div>
    </BaseDropdown>
  );
};
